import { GameState } from '../types';
import { DiceType, getDiceName, getDicePrice, getDiceSellPrice, getDiceMaxOwned } from '../models/DiceModel';
import { GroschenService } from '../services/GroschenService';
import { t } from '../i18n';

interface UseDiceShopParams {
  gameState: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState>>;
  setShowModal: (show: boolean) => void;
  setModalContent: (content: { title: string; content: string }) => void;
}

export const useDiceShop = ({
  gameState,
  setGameState,
  setShowModal,
  setModalContent,
}: UseDiceShopParams) => {
  const handleBuyDice = (diceType: DiceType) => {
    const price = getDicePrice(diceType);

    if (gameState.players[0].groschen < price) {
      setModalContent({
        title: t('insufficientGroschenTitle'),
        content: t('insufficientGroschenContent', { price }),
      });
      setShowModal(true);
      return;
    }

    const ownedCount = gameState.players[0].ownedDice.filter(type => type === diceType).length;
    const maxOwned = getDiceMaxOwned(diceType);
    if (ownedCount >= maxOwned) {
      setModalContent({
        title: t('maxQuantityTitle'),
        content: t('maxQuantityContent', { max: maxOwned }),
      });
      setShowModal(true);
      return;
    }

    const updatedOwnedDice = [...gameState.players[0].ownedDice, diceType];

    const updatedPlayer = GroschenService.reduceGroschen(
      gameState.players[0],
      gameState.diceConfigs,
      price,
      `buy_dice_${getDiceName(diceType)}`,
      gameState.settings
    );

    const playerWithUpdatedDice = {
      ...updatedPlayer,
      ownedDice: updatedOwnedDice,
    };

    setGameState(prevState => {
      const updatedPlayers = [...prevState.players];
      updatedPlayers[0] = playerWithUpdatedDice;
      return { ...prevState, players: updatedPlayers };
    });
  };

  const handleSellDice = (diceType: DiceType) => {
    setGameState(prevState => {
      const player = prevState.players[0];
      const sellPrice = getDiceSellPrice(diceType);

      const usedCount = prevState.diceConfigs.diceConfigs.filter(type => type === diceType).length;
      const ownedCount = player.ownedDice.filter(type => type === diceType).length;

      if (ownedCount <= usedCount) {
        return prevState;
      }

      const indexToRemove = player.ownedDice.findIndex((type, index) => {
        if (type === diceType) {
          const countUpToIndex = player.ownedDice.slice(0, index + 1).filter(t => t === diceType).length;
          return countUpToIndex > usedCount;
        }
        return false;
      });

      if (indexToRemove === -1) {
        return prevState;
      }

      const updatedOwnedDice = [...player.ownedDice];
      updatedOwnedDice.splice(indexToRemove, 1);

      const updatedPlayer = GroschenService.addGroschen(
        { ...player, ownedDice: updatedOwnedDice },
        prevState.diceConfigs,
        sellPrice,
        `sell_dice_${getDiceName(diceType)}`,
        prevState.settings
      );

      const updatedPlayers = [...prevState.players];
      updatedPlayers[0] = updatedPlayer;

      return { ...prevState, players: updatedPlayers };
    });
  };

  return { handleBuyDice, handleSellDice };
};
